import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import informationAPI from '../../api/informationAPI';
import getAsyncErrorMessage from '../../utils/getAsyncErrorMessage';
import { GENERAL_ERROR_MESSAGE } from '../../constants';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AppThunk } from '../store';
import type { RejectedWithValueAction } from '../../types/common';
import type { ModalMode, InformationType, CreateInformationParams, UpdateInformationParamsClient, DeleteInformationParams, InformationState } from '../../types/information';

export const initialState: InformationState = {
  isLoading: false,
  hasError: false,
  serverMessage: undefined,
  dialogMode: undefined,
  isDialogOpen: false,
  information: undefined,
  informations: undefined,
};

const slice = createSlice({
  name: 'Information',
  initialState,
  reducers: {
    resetServerMessage(state: InformationState) {
      state.isLoading = false;
      state.serverMessage = '';
      state.hasError = false;
    },

    setServerMessage(state: InformationState, action: PayloadAction<string>): void {
      state.isLoading = false;
      state.serverMessage = action.payload;
    },

    setDialogMode(state: InformationState, action: PayloadAction<string>): void {
      state.dialogMode = action.payload;
    },

    openDialog(state: InformationState): void {
      state.isDialogOpen = true;
    },

    closeDialog(state: InformationState): void {
      state.isDialogOpen = false;
      state.dialogMode = undefined;
      state.information = undefined;
    },

    selectInformation(state: InformationState, action: PayloadAction<InformationType>): void {
      if (action.payload !== undefined) {
        const information = action.payload;
        const selectedInformation: InformationType | undefined = state.informations.find((org) => org.id === information.id);
        if (selectedInformation !== undefined) {
          state.information = selectedInformation;
        } else {
          state.information = action.payload;
        }
      } else {
        state.information = action.payload;
      }
    },
  },

  extraReducers: (builder) => {
    builder
      // Get Informations
      // ----------------------------------------
      .addCase(getInformations.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getInformations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.informations = action.payload;
        // state.informations = action.payload.sort((a, b) => {
        //   // https://developer.mozilla.org/ja/docs/Web/JavaScript/Reference/Global_Objects/Array/sort
        //   if (a.isDraft < b.isDraft) {
        //     return -1;
        //   }
        //   if (a.isDraft > b.isDraft) {
        //     return 1;
        //   }
        //   if (a.order < b.order) {
        //     return -1;
        //   }
        //   if (a.order > b.order) {
        //     return 1;
        //   }
        //   // names must be equal
        //   return 0;
        // });
      })
      .addCase(getInformations.rejected, (state, action) => {
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<undefined, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
        state.informations = state.informations === undefined ? [] : state.informations;
      })

      .addCase(getInformationsForManage.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getInformationsForManage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.informations = action.payload;
        // state.informations = action.payload.sort((a, b) => {
        //   // https://developer.mozilla.org/ja/docs/Web/JavaScript/Reference/Global_Objects/Array/sort
        //   if (a.isDraft < b.isDraft) {
        //     return -1;
        //   }
        //   if (a.isDraft > b.isDraft) {
        //     return 1;
        //   }
        //   if (a.order < b.order) {
        //     return -1;
        //   }
        //   if (a.order > b.order) {
        //     return 1;
        //   }
        //   // names must be equal
        //   return 0;
        // });
      })
      .addCase(getInformationsForManage.rejected, (state, action) => {
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<undefined, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
        state.informations = state.informations === undefined ? [] : state.informations;
      })

      // Create Informations
      // ----------------------------------------
      .addCase(createInformation.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createInformation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.informations = state.informations === undefined ? [action.payload] : [...state.informations, action.payload];
      })
      .addCase(createInformation.rejected, (state, action) => {
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<CreateInformationParams, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
        state.informations = state.informations === undefined ? [] : state.informations;
      })
      // Update Informations
      // ----------------------------------------
      .addCase(updateInformation.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateInformation.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedInformation = action.payload;
        state.informations = state.informations.map((information) => {
          if (information.id === updatedInformation.id) {
            return updatedInformation;
          } else {
            return information;
          }
        });
      })
      .addCase(updateInformation.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<UpdateInformationParamsClient, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
        state.informations = state.informations === undefined ? [] : state.informations;
      })
      // Delete Informations
      // ----------------------------------------
      .addCase(deleteInformation.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteInformation.fulfilled, (state, action) => {
        state.isLoading = false;
        const deletedInformation = action.payload;
        state.information = undefined;
        state.informations = state.informations.filter((information) => {
          if (information.id === deletedInformation.id) {
            return false;
          } else {
            return true;
          }
        });
      })
      .addCase(deleteInformation.rejected, (state, action) => {
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<DeleteInformationParams, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
        state.information = undefined;
        state.informations = state.informations === undefined ? [] : state.informations;
      });
  },
});

export const { reducer, actions } = slice;

export const resetServerMessage =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.resetServerMessage());
  };

export const setDialogMode =
  (mode?: ModalMode): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.setDialogMode(mode));
  };

export const openDialog =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.openDialog());
  };

export const closeDialog =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.closeDialog());
  };

export const selectInformation =
  (information?: InformationType): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.selectInformation(information));
  };

export const getInformations = createAsyncThunk('Information/getInformations', async (_, thunkAPI) => {
  try {
    const response = await informationAPI.getInformations();
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const getInformationsForManage = createAsyncThunk('Information/getInformationsForManage', async (_, thunkAPI) => {
  try {
    const response = await informationAPI.getInformationsForManage();
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const createInformation = createAsyncThunk('Information/createInformation', async (params: CreateInformationParams, thunkAPI) => {
  try {
    const response = await informationAPI.createInformation(params);
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const updateInformation = createAsyncThunk('Information/updateInformation', async (params: UpdateInformationParamsClient, thunkAPI) => {
  try {
    const response = await informationAPI.updateInformation(params);
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const deleteInformation = createAsyncThunk('Information/deleteInformation', async (params: DeleteInformationParams, thunkAPI) => {
  try {
    const response = await informationAPI.deleteInformation(params);
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export default slice;
